import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import LightGestion from 'components/headers/lightGestion';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

const load = (k, def) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : def; } catch { return def; } };

const toAbsImageUrl = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  if (s.startsWith('data:') || s.startsWith('blob:') || /^https?:\/\//i.test(s)) return s;
  const BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';
  if (s.startsWith('/storage/')) return `${BASE}${s}`;
  if (s.startsWith('storage/')) return `${BASE}/${s}`;
  if (s.startsWith('caisse/')) return `${BASE}/storage/${s}`;
  return `${BASE}/${s.replace(/^\//, '')}`;
};

const Devis = () => {
  const { t } = useTranslation();
  const [showType, setShowType] = useState(true);
  const [type, setType] = useState('produit'); 
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState({ id: null, name: '', address: '', phone: '', ice: '' });
  const [rows, setRows] = useState([{ designation: '', tva: 0, pu: 0, qty: 1 }]);
  const [refNo, setRefNo] = useState(() => `DEV-${new Date().getFullYear()}-${String(Math.floor(Math.random()*1000)).padStart(4,'0')}`);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [previewOpen, setPreviewOpen] = useState(false);
  const quoteRef = useRef(null);
  const [company, setCompany] = useState(load('company.info', { name: '', phone: '', email: '', ice: '', website: '', address: '', logo: '', rc: '', if: '', tp: ''}));

  const currency = useCallback((v) => `${Number(v || 0).toFixed(2)} ${t('stock.currency', 'MAD')}`,[t]);

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/clientgestions', {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || res.data.clients || []);
      const data = (raw || []).map(r => ({
        id: r.id || r.client_id || r._id,
        name: String(r.name || r.Nom || r.nom || ''),
        address: r.Address || r.address || '',
        phone: r.Telephone || r.phone || '',
        ice: r.ICE || r.ice || ''
      }));
      setClients(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

const getSociete = async () => {
  const token = localStorage.getItem('authToken');
  const res = await axios.get(
    'https://plateformewmg.cloud/api/Gestion/societes',
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    }
  );
  return res.data?.data?.[0] || null;
}; 
useEffect(() => {
  getSociete().then((raw) => {
    if (!raw) return;

    setCompany({
      name: raw.Nom || '',
      phone: raw.Telephone || '',
      address: raw.Address || '',
      website: raw.SiteWeb || '',
      email: raw.Email || '',
      rc: raw.RC || '',
      ice: raw.ICE || '',
      if: raw.IF || '',
      tp: raw.TP || '',
      logo: raw.logo_url || ''
    });
  });
}, []);

  const addRow = () => setRows(r => [...r, { designation: '', tva: 0, pu: 0, qty: 1 }]);
  const delRow = (idx) => setRows(r => r.filter((_,i) => i !== idx));
  const setRow = (idx, patch) => setRows(r => r.map((row,i) => i===idx ? { ...row, ...patch } : row));

  const totals = useMemo(() => {
    const ht = rows.reduce((s, r) => s + Number(r.pu||0)*Number(r.qty||0), 0);
    const tva = rows.reduce((s, r) => s + (Number(r.pu||0)*Number(r.qty||0)) * (Number(r.tva||0)/100), 0);
    const ttc = ht + tva;
    return { ht, tva, ttc };
  }, [rows]);

  const waitForImages = useCallback(async (rootEl) => {
    if (!rootEl) return;
    const imgs = Array.from(rootEl.querySelectorAll('img'));
    await Promise.all(imgs.map(img => new Promise(resolve => {
      if (img.complete && img.naturalWidth > 0) return resolve();
      img.onload = img.onerror = resolve;
    })));
  }, []);

  const handleDownload = async () => {
    try {
      const node = quoteRef.current;
      if (!node) return;
      await waitForImages(node);
      
      // 1. Reduced scale from 3 to 2 (cuts the pixel resolution in half while remaining ultra-crisp)
      const canvas = await html2canvas(node, { 
        scale: 2, 
        backgroundColor: '#ffffff', 
        useCORS: true,
        logging: false // turning off console spam
      });
      
      // 2. Swapped 'image/png' to 'image/jpeg' and introduced a 75% compression quality ratio
      const imgData = canvas.toDataURL('image/jpeg', 0.75);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // 3. Changed compression descriptor flag to 'JPEG'
      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, (canvas.height * pageWidth) / canvas.width);
      pdf.save(`${refNo}.pdf`);
    } catch (err) { 
      console.error(err); 
    }
  };
  return (
    <AnimationRevealPage>
      <LightGestion />

      <div className="container app-content" style={{ maxWidth: 1000, marginTop: '-60px' }}>
        {/* Formulaire de saisie */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div style={{ fontWeight: 900, fontSize: '30px' }}>{t('quote.title', 'Créer un devis')}</div>
            <Button variant="outline-primary" onClick={() => setShowType(true)}>{t('quote.changeType', 'Changer le type')}</Button>
          </div>

          <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 12, padding: 20 }}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">{t('quote.reference', 'Référence')}</label>
                <input className="form-control" value={refNo} onChange={e => setRefNo(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t('quote.date', 'Date')}</label>
                <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t('quote.client', 'Client')}</label>
                <Form.Control list="client-list-devis" placeholder={t('quote.clientPlaceholder', 'Sélectionner...')} value={client?.name || ''} onChange={(e) => {
                  const name = e.target.value; const found = clients.find(x => x.name.toLowerCase() === name.toLowerCase());
                  if (found) setClient(found); else setClient({ name });
                }} />
                <datalist id="client-list-devis">{clients.map(c => (<option key={c.id} value={c.name} />))}</datalist>
              </div>
            </div>

            <div className="table-responsive mt-4">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>{t('quote.table.designation', 'Désignation')}</th>
                    <th>TVA %</th>
                    <th>PU HT</th>
                    <th>Qté</th>
                    <th className="text-end">Total HT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx}>
                      <td><input className="form-control" value={r.designation} onChange={e => setRow(idx, { designation: e.target.value })} /></td>
                      <td style={{ width: 100 }}>
                        <select className="form-select" value={r.tva} onChange={e => setRow(idx, { tva: Number(e.target.value) })}>
                          <option value={0}>0%</option><option value={10}>10%</option><option value={20}>20%</option>
                        </select>
                      </td>
                      <td style={{ width: 120 }}><input type="number" className="form-control" value={r.pu} onChange={e => setRow(idx, { pu: Number(e.target.value) })} /></td>
                      <td style={{ width: 100 }}><input type="number" className="form-control" value={r.qty} onChange={e => setRow(idx, { qty: Number(e.target.value) })} /></td>
                      <td className="text-end fw-bold">{currency(r.pu * r.qty)}</td>
                      <td className="text-end"><Button variant="link" className="text-danger" onClick={() => delRow(idx)}>✕</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button variant="outline-primary" size="sm" onClick={addRow}>+ {t('quote.table.addLine','Ajouter une ligne')}</Button>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <div style={{ minWidth: 250, borderTop: '2px solid #eee', paddingTop: 10 }}>
                <div className="d-flex justify-content-between"><span>HT</span><span className="fw-bold">{currency(totals.ht)}</span></div>
                <div className="d-flex justify-content-between"><span>TVA</span><span className="fw-bold">{currency(totals.tva)}</span></div>
                <div className="d-flex justify-content-between fs-5 mt-2"><span>TOTAL TTC</span><span className="fw-bold text-primary">{currency(totals.ttc)}</span></div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button variant="secondary" onClick={() => setPreviewOpen(true)}>{t('quote.buttons.preview','Aperçu')}</Button>
              <Button variant="primary" onClick={handleDownload}>{t('quote.buttons.download','Télécharger PDF')}</Button>
            </div>
          </div>
        </div>

        {/* SECTION PDF (Format A4) */}
        <div 
          ref={quoteRef}
          style={{
            background: '#ffffff',
            color: '#000',
            padding: '15mm',
            width: '210mm',
            minHeight: '297mm',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          <div style={{ flex: 1 }}>
            
            {/* Header PDF */}
            <div className="d-flex justify-content-between align-items-start mb-5">
              {company.logo ? (
                <img src={company.logo} alt="logo" crossOrigin="anonymous" style={{ height: 80, width: 80, objectFit: 'contain' }} />
              ) : (
                <h2 style={{ margin: 0 }}>{company.name || 'WMG'}</h2>
              )}
              <div style={{ textAlign: 'right' }}>
                <h3 style={{ margin: 0, color: '#2563eb' }}>{t('quote.pdf.quote','DEVIS')}</h3>
                <div style={{ fontSize: 13 }}>{t('quote.pdf.ref','Réf :')} {refNo}</div>
                <div style={{ fontSize: 13 }}>{t('quote.pdf.date','Date :')} {new Date(date).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Parties */}
            <div className="row mb-5" style={{ fontSize: 13 }}>
              <div className="col-6">
                <div style={{ borderLeft: '3px solid #2563eb', paddingLeft: 10 }}>
                  <div className="fw-bold text-uppercase" style={{ color: '#666', fontSize: 11 }}>{t('quote.pdf.emitter','De')}</div>
                  <div className="fw-bold fs-6">{company.name}</div>
                  <div>{company.email}</div>
                  <div>{company.phone}</div>
                  <div>{company.website}</div>

                </div>
              </div>
              <div className="col-6 text-end">
                <div style={{ borderRight: '3px solid #ddd', paddingRight: 10 }}>
                  <div className="fw-bold text-uppercase" style={{ color: '#666', fontSize: 11 }}>{t('quote.pdf.addressedTo','Pour')}</div>
                  <div className="fw-bold fs-6">{client?.name}</div>
                  <div>{client.address}</div>
                  {client.ice && <div>ICE: {client.ice}</div>}
                </div>
              </div>
            </div>

            {/* Table Articles */}
            <table className="table table-sm" style={{ fontSize: 12 }}>
              <thead style={{ backgroundColor: '#f8fafc' }}>
                <tr>
                  <th className="py-2">{t('quote.pdf.designation','Désignation')}</th>
                  <th className="text-end py-2">TVA</th>
                  <th className="text-end py-2">PU HT</th>
                  <th className="text-end py-2">Qté</th>
                  <th className="text-end py-2">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2">{r.designation}</td>
                    <td className="text-end py-2">{r.tva}%</td>
                    <td className="text-end py-2">{Number(r.pu).toFixed(2)}</td>
                    <td className="text-end py-2">{r.qty}</td>
                    <td className="text-end py-2 fw-bold">{Number(r.pu * r.qty).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totaux PDF */}
            <div className="d-flex justify-content-end mt-4">
              <div style={{ width: 220, fontSize: 13 }}>
                <div className="d-flex justify-content-between py-1 border-bottom"><span>Total HT</span><span>{Number(totals.ht).toFixed(2)}</span></div>
                <div className="d-flex justify-content-between py-1 border-bottom"><span>TVA</span><span>{Number(totals.tva).toFixed(2)}</span></div>
                <div className="d-flex justify-content-between py-2 fw-bold fs-6"><span>TOTAL TTC</span><span>{Number(totals.ttc).toFixed(2)} MAD</span></div>
              </div>
            </div>

            {/* Signature à DROITE */}
            <div className="d-flex justify-content-end mt-5">
              <div style={{ border: '1px solid #eee', borderRadius: 8, height: 100, padding: 12, fontSize: 11, color: '#999', width: 280 }}>
                {t('quote.pdf.clientSignature','Signature et cachet du client')}
              </div>
            </div>

          </div>

          {/* Footer PDF */}
          <div style={{ marginTop: 'auto', paddingTop: 15, borderTop: '2px solid #000000', textAlign: 'center', fontSize: 10 }}>
            <div>{company.address} | Tél: {company.phone} | {company.email}</div>
            <div style={{ color: '#666', marginTop: 4 }}>
              {company.rc && `RC: ${company.rc} `} {company.if && `| IF: ${company.if} `} 
              RC: {company.rc} | IF: {company.if} | ICE: {company.ice}| Patente: {company.tp}
            </div>
          </div>
        </div>
      </div>

      {/* Modals existantes... */}
      <Modal show={showType} onHide={() => setShowType(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t('quote.modals.typeTitle','Type de devis')}</Modal.Title></Modal.Header>
        <Modal.Body className="d-grid gap-2 p-4">
          <Button size="lg" onClick={() => { setType('produit'); setShowType(false); }}>📦 {t('quote.type.product','Produit')}</Button>
          <Button size="lg" variant="outline-primary" onClick={() => { setType('service'); setShowType(false); }}>🛠️ {t('quote.type.service','Service')}</Button>
        </Modal.Body>
      </Modal>

      <Modal show={previewOpen} onHide={() => setPreviewOpen(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Aperçu du Devis</Modal.Title></Modal.Header>
        <Modal.Body className="bg-light text-center">
          <p className="text-muted small">Ceci est une prévisualisation de votre document A4.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewOpen(false)}>Fermer</Button>
          <Button variant="primary" onClick={handleDownload}>Télécharger PDF</Button>
        </Modal.Footer>
      </Modal>

    </AnimationRevealPage>
  );
};

export default Devis;